import Image from "next/image";
import Link from "next/link";
import {
  IconBookmark,
  IconBookmarkFilled,
  IconCalendar,
  IconCreditCard,
  IconEmail,
  IconGraduationCap,
  IconLocationPin,
  IconOnline,
  IconPerson,
  IconTelephone,
  IconVerified,
} from "../../styles/icons";
import {
  Button,
  Popover,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/react";
import React, { useMemo, useCallback, useState } from "react";
import { useUtilityDataSelector } from "../utilities/contexts";
import { useSelector, useDispatch } from "react-redux";
import { updateProfileBookmarks } from "../redux/actions/bookmarks";
import { deleteData, postData } from "../utilities/axios";
import { useToast, Spinner, useBreakpointValue } from "@chakra-ui/react";
import { closestTo } from "date-fns";
import { serializeJsonLd } from "../utilities/helpers";

const sessionIcons = {
  1: <IconPerson />,
  3: <IconOnline />,
  2: <IconTelephone />,
  4: <IconEmail />,
};

const days = ["M", "T", "W", "T", "F", "S", "S"];

const sanitizeSummary = (summary) => {
  if (typeof summary !== "string") return summary;

  // Keep server/client output identical to avoid hydration mismatches.
  return summary
    .replace(/<script[^>]*>([\S\s]*?)<\/script>/gim, "")
    .replace(/<\/?\w(?:[^"'>]|"[^"]*"|'[^']*')*>/gim, "");
};

export const PractitionerCard = ({
  practitioner,
  onShowLogin,
  onOpenRequest,
  selectedPracticeAreas,
}) => {
  const utilities = useUtilityDataSelector((utilityData) => utilityData);
  const savedProfiles = useSelector((state) => state.bookmarks.profiles);
  const token = useSelector((state) => state.user.JWTToken);
  const dispatch = useDispatch();
  const toast = useToast();
  const imageSize = useBreakpointValue({
    base: 150,
    sm: 150,
    md: 70,
    lg: 150,
  });

  const [loadingSave, setLoadingSave] = useState(false);

  const isBookmarked = useMemo(() => {
    return (
      savedProfiles.filter(
        (profile) => profile.profileId === practitioner.profileId,
      ).length > 0
    );
  }, [savedProfiles, practitioner.profileId]);

  const hasLocation = useMemo(() => {
    if (practitioner?.addresses?.length > 0) {
      let closest = practitioner.addresses[0];
      let second = null;
      if (practitioner.addresses.length > 1) {
        second = practitioner.addresses[1];
        if (closest.distance > practitioner.addresses[1].distance) {
          closest = practitioner.addresses[1];
          second = practitioner.addresses[0];
        }
      }

      return (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <span>
            {!isNaN(closest.distance) && practitioner.distance === 1 && (
              <>
                <b className="font-bold">{closest.distance.toFixed(1)} miles</b>{" "}
                away,{" "}
              </>
            )}
            {closest.city}, {closest.postcode.split(" ")[0]}
          </span>
          {second && <p className="font-bold mx-2">|</p>}
          {second && (
            <span>
              {second.city}, {second.postcode.split(" ")[0]}
            </span>
          )}
        </div>
      );
    }

    return "Online";
  }, [practitioner]);

  const decodedSummary = useMemo(() => {
    return sanitizeSummary(practitioner.summary);
  }, [practitioner.summary]);

  const removeFromSaved = useCallback(async () => {
    try {
      await deleteData(
        token,
        {
          profileId: practitioner.profileId,
          practitionerType: "therapist",
        },
        `${process.env.NEXT_PUBLIC_NEW_SEARCH_URL}save`,
        process.env.NEXT_PUBLIC_NEW_SEARCH_APIKEY,
      );

      dispatch(
        updateProfileBookmarks(
          savedProfiles.filter(
            (profile) => profile.profileId !== practitioner.profileId,
          ),
        ),
      );
    } catch (e) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again later.",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }

    setLoadingSave(false);
  }, [dispatch, practitioner.profileId, savedProfiles, toast, token]);

  const addToSaved = useCallback(async () => {
    try {
      await postData(
        token,
        {
          profileId: practitioner.profileId,
          practitionerType: "therapist",
        },
        `${process.env.NEXT_PUBLIC_NEW_SEARCH_URL}save`,
        process.env.NEXT_PUBLIC_NEW_SEARCH_APIKEY,
      );

      dispatch(updateProfileBookmarks([...savedProfiles, practitioner]));
    } catch (e) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again later.",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }

    setLoadingSave(false);
  }, [practitioner, dispatch, savedProfiles, toast, token]);

  const attemptSaveAction = useCallback(() => {
    if (!token) {
      if (onShowLogin) {
        onShowLogin();
      }

      return;
    }

    setLoadingSave(true);

    if (isBookmarked) {
      removeFromSaved();
    } else {
      addToSaved();
    }
  }, [token, isBookmarked, onShowLogin, removeFromSaved, addToSaved]);

  const practiceAreas = useMemo(() => {
    if (!selectedPracticeAreas) {
      return practitioner.practiceAreas;
    }

    const selectedAreas = selectedPracticeAreas.map((area) => area.value);

    const areas = practitioner.practiceAreas.sort((a, b) =>
      selectedAreas.includes(parseInt(a.value, 10))
        ? -1
        : selectedAreas.includes(parseInt(b.value, 10))
          ? 1
          : 0,
    );

    return areas.map((area) => ({
      ...area,
      searched: selectedAreas.includes(parseInt(area.value, 10)),
    }));
  }, [selectedPracticeAreas, practitioner.practiceAreas]);

  const profileLink = `/counsellors/${practitioner.pageName}`;
  const publicSiteUrl =
    process.env.NEXT_PUBLIC_WEBSITE_URL || "https://welldoing.org";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${publicSiteUrl}${profileLink}`,
    name: practitioner?.fullname,
    jobTitle: practitioner?.therapistTitles?.map((title) => title.label),
    knowsAbout: practiceAreas?.map((practiceArea) => practiceArea.label),
    description: decodedSummary,
    address: {
      "@type": "PostalAddress",
      postalCode: practitioner?.addresses[0]?.postcode,
      streetAddress: practitioner?.addresses[0]?.address,
      addressRegion: practitioner?.addresses[0]?.city,
    },
    url: `${publicSiteUrl}${profileLink}`,
    email: practitioner?.contactEmail,
    knowsLanguage: practitioner?.languages?.map((language) => ({
      "@type": "Language",
      name: language.label,
    })),
    gender: practitioner?.gender,
    image: practitioner?.profileImage,
    sameAs: practitioner?.links?.map((link) => link.url),
  };

  return (
    <div className="rounded-3xl shadow-lg overflow-hidden flex flex-col p-[15px] gap-2">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: serializeJsonLd(jsonLd),
        }}
      />
      <div className="flex gap-4 w-full">
        <Link prefetch={false} href={profileLink}>
          <div className="w-[70px] sm:w-[120px] md:w-[70px] lg:w-[120px] h-[70px] sm:h-[100px] md:h-[70px] lg:h-[120px]  rounded-full overflow-hidden bg-gray-50">
            <Image
              src={practitioner.profileImage ?? "/images/profileHeader.jpg"}
              alt={practitioner.fullname}
              width={imageSize}
              height={imageSize}
              style={{ objectFit: "cover" }}
              unoptimized
            />
          </div>
        </Link>
        <div className="w-full flex-1 flex flex-col gap-2 pb-4">
          <div className="flex flex-col gap-2.5 flex-1">
            <div className="flex items-center gap-2">
              <Link prefetch={false} href={profileLink}>
                <h3 className="text-lg font-bold text-brand-text">
                  {practitioner.fullname}
                </h3>
              </Link>
              {practitioner.verified !== 0 && (
                <IconVerified size="24px" color="#3F3B39" />
              )}
            </div>
            <h4 className="text-brand-alt font-bold">
              {practitioner.therapistTitles
                .map((title) => title.label)
                .join(", ")}
            </h4>
          </div>
          <div className="hidden sm:flex md:hidden lg:flex gap-2 flex-wrap">
            {practiceAreas.slice(0, 2).map((practiceArea) => (
              <div
                className={`${practiceArea.searched ? "bg-brand-alt text-brand-off" : "bg-brand-off text-brand-alt"} py-1.5 px-3 rounded-full font-bold w-max text-sm`}
                key={`therapistPracticeArea_${practiceArea.value}`}
              >
                {practiceArea.label}
              </div>
            ))}
            {practiceAreas.length > 2 && (
              <Popover className="relative">
                <PopoverButton
                  className={`${selectedPracticeAreas?.length > 2 ? "bg-brand-alt text-brand-off" : "bg-brand-off text-brand-alt"} py-1.5 px-3 rounded-full font-bold w-max text-sm`}
                >
                  + {practiceAreas.length - 2} more
                </PopoverButton>
                <PopoverPanel
                  className="p-2 rounded-md shadow-lg border border-brand-off bg-white"
                  anchor="bottom"
                >
                  <div className="flex flex-wrap gap-2 max-w-[350px]">
                    {practiceAreas.slice(2).map((practiceArea) => (
                      <div
                        className={`${practiceArea.searched ? "bg-brand-alt text-brand-off" : "bg-brand-off text-brand-alt"} py-1.5 px-3 rounded-full flex flex-1 justify-center min-w-max font-bold w-max text-sm`}
                        key={`therapistPracticeArea_${practiceArea.value}`}
                      >
                        {practiceArea.label}
                      </div>
                    ))}
                  </div>
                </PopoverPanel>
              </Popover>
            )}
          </div>
        </div>
      </div>
      <div className="flex sm:hidden md:flex lg:hidden gap-2 flex-wrap">
        {practiceAreas.slice(0, 2).map((practiceArea) => (
          <div
            className={`${practiceArea.searched ? "bg-brand-alt text-brand-off" : "bg-brand-off text-brand-alt"} py-1 px-2 sm:py-2 sm:px-4 rounded-full font-bold w-max text-xs sm:text-sm`}
            key={`therapistPracticeArea_${practiceArea.value}`}
          >
            {practiceArea.label}
          </div>
        ))}
        {practiceAreas.length > 2 && (
          <Popover className="relative">
            <PopoverButton
              className={`${selectedPracticeAreas?.length > 2 ? "bg-brand-alt text-brand-off" : "bg-brand-off text-brand-alt"} py-1 px-2 sm:py-2 sm:px-4 rounded-full font-bold w-max text-xs sm:text-sm`}
            >
              + {practiceAreas.length - 2} more
            </PopoverButton>
            <PopoverPanel
              className="p-2 rounded-md shadow-lg border border-brand-off bg-white"
              anchor="bottom"
            >
              <div className="flex flex-wrap gap-2 max-w-[350px]">
                {practiceAreas.slice(2).map((practiceArea) => (
                  <div
                    className={`${practiceArea.searched ? "bg-brand-alt text-brand-off" : "bg-brand-off text-brand-alt"} py-1 px-2 sm:py-2 sm:px-4 rounded-full font-bold w-max text-xs sm:text-sm`}
                    key={`therapistPracticeArea_${practiceArea.value}`}
                  >
                    {practiceArea.label}
                  </div>
                ))}
              </div>
            </PopoverPanel>
          </Popover>
        )}
      </div>
      <div className="flex flex-col gap-6 sm:gap-5 md:gap-6 lg:gap-5">
        <p className="w-full whitespace-break-spaces text-sm text-gray-600">
          {decodedSummary}
        </p>
        <div className="flex flex-col sm:flex-row md:flex-col lg:flex-row items-start sm:items-center md:items-start lg:items-center gap-[10px] sm:gap-10 md:gap-[10px] lg:gap-10">
          <div className="gap-[7px] flex items-center">
            <IconLocationPin />
            <span className="text-sm font-light">{hasLocation}</span>
          </div>
          <div
            itemScope
            itemType="https://schema.org/Offer"
            className="gap-[7px] flex items-center"
          >
            <IconCreditCard />
            <span className="text-sm font-light">
              Sessions from{" "}
              <b className="font-bold">£{practitioner.averagePrice}</b>
            </span>
          </div>
          <Popover
            itemScope
            itemType="https://schema.org/Schedule"
            className="relative"
          >
            <PopoverButton className="gap-[7px] flex items-center cursor-pointer">
              <IconCalendar />
              <span className="text-brand text-sm font-bold">
                See working schedule
              </span>
            </PopoverButton>
            <PopoverPanel
              className="p-2 rounded-md shadow-lg border border-brand-off flex flex-col bg-white"
              anchor="bottom"
            >
              <h4 className="mb-4 text-bold w-full text-center">
                Work schedule
              </h4>
              <div className="grid grid-cols-[110px_1fr]">
                <div />
                <div className="grid grid-cols-7 gap-1">
                  {days.map((day, i) => (
                    <p
                      key={`weekDay_${i}`}
                      className="text-sm font-bold text-center h-[30px]"
                    >
                      {day}
                    </p>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-[110px_1fr]">
                <div className="flex flex-col gap-1">
                  {utilities.availability.map((label, i) =>
                    label && label !== "" ? (
                      <p
                        className="font-bold text-sm h-8 flex items-center"
                        key={`timeName_${i}`}
                      >
                        {label}
                      </p>
                    ) : null,
                  )}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {practitioner.availability.map((availSet, id) =>
                    availSet.map((avail, day) => (
                      <div
                        key={`day_${id}_${day}`}
                        className={`w-8 h-8 text-white text-sm ${avail ? "bg-brand" : "bg-brand-off"} rounded-[5px] flex justify-center items-center py-1 px-3`}
                      >
                        <p className="absolute">{avail ? "\u2714" : ""}</p>
                      </div>
                    )),
                  )}
                </div>
              </div>
            </PopoverPanel>
          </Popover>
          {(practitioner?.isSupervisor || practitioner?.isSupervisor === 1) && (
            <div className="gap-[7px] flex items-center py-1.5">
              <IconGraduationCap />
              <span className="text-sm">Supervisor</span>
            </div>
          )}
        </div>
        <div className="flex flex-col-reverse sm:flex-row md:flex-col-reverse lg:flex-row items-start sm:items-end md:items-start lg:items-end gap-6 sm:justify-between">
          <div className="flex w-full sm:hidden md:flex lg:hidden flex-col gap-2.5">
            <div className="w-full flex gap-1">
              <Button
                disabled={loadingSave}
                onClick={attemptSaveAction}
                className={`rounded-full text-sm w-full flex px-3 py-1.5 items-center justify-center text-brand cursor-pointer font-bold ${isBookmarked ? "bg-brand hover:bg-brand/60" : "bg-white/10 hover:bg-brand/10 border border-brand"} transition`}
              >
                {loadingSave ? (
                  <Spinner color={isBookmarked ? "white" : "green"} />
                ) : isBookmarked ? (
                  <IconBookmarkFilled color="white" />
                ) : (
                  <IconBookmark />
                )}
                {isBookmarked ? "Remove" : "Bookmark"}
              </Button>
              <Link prefetch={false} href={profileLink} className="w-full">
                <Button className="rounded-full text-sm w-full flex-1 px-3 py-1.5 border border-brand text-brand cursor-pointer font-bold bg-white/10 hover:bg-brand/10 transition">
                  View profile
                </Button>
              </Link>
            </div>
            <Button
              onClick={() => onOpenRequest(practitioner)}
              className="rounded-full px-3 py-1.5 text-white cursor-pointer font-bold bg-brand hover:bg-brand/60 transition"
              id="make-contact-button-2"
            >
              Make contact
            </Button>
          </div>
          <div className="hidden sm:flex md:hidden lg:flex flex-row gap-2.5">
            <Link prefetch={false} href={profileLink}>
              <Button className="rounded-full px-3 py-1.5 border border-brand text-brand cursor-pointer font-bold bg-white/10 hover:bg-brand/10 transition">
                View profile
              </Button>
            </Link>
            <Button
              onClick={() => onOpenRequest(practitioner)}
              className="rounded-full px-3 py-1.5 text-white cursor-pointer font-bold bg-brand hover:bg-brand/60 transition"
              id="make-contact-button"
            >
              Make contact
            </Button>
            <Button
              disabled={loadingSave}
              onClick={attemptSaveAction}
              className={`rounded-full h-[38px] w-[38px] flex items-center justify-center text-brand cursor-pointer font-bold ${isBookmarked ? "bg-brand hover:bg-brand/60" : "bg-white/10 hover:bg-brand/10 border border-brand"} transition`}
            >
              {loadingSave ? (
                <Spinner color={isBookmarked ? "white" : "green"} />
              ) : isBookmarked ? (
                <IconBookmarkFilled color="white" />
              ) : (
                <IconBookmark />
              )}
            </Button>
          </div>
          <div className="flex flex-row gap-2.5">
            {practitioner.sessionTypes.map((type) => (
              <div
                key={`therapistSessionType_${type.value}`}
                className="relative bg-gray-100 h-8 w-8 flex items-center justify-center rounded-full text-brand-text"
              >
                {sessionIcons[type.value]}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
